import { useState } from "react";
import { useAuth, useEvent } from "@godgpt/react";

interface EventLogEntry {
  id: string;
  time: string;
  type: string;
  data: string;
}

export function AuthPlayground() {
  const { isAuthenticated, user, signIn, signOut, isLoading } = useAuth();
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [signingIn, setSigningIn] = useState<string | null>(null);

  // Subscribe to auth events
  useEvent("auth:signIn", (...args: unknown[]) => {
    const data = args[0] as { user: { email: string } };
    addEvent("auth:signIn", `User signed in: ${data?.user?.email}`);
  });

  useEvent("auth:signOut", () => {
    addEvent("auth:signOut", "User signed out");
  });

  useEvent("auth:error", (...args: unknown[]) => {
    const data = args[0] as { error: { message: string } };
    addEvent("auth:error", `Error: ${data?.error?.message}`);
  });

  function addEvent(type: string, data: string) {
    const now = new Date();
    const time = now.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    setEvents((prev) => [
      { id: `${Date.now()}-${Math.random()}`, time, type, data },
      ...prev.slice(0, 49), // Keep last 50 events
    ]);
  }

  async function handleSignIn(provider: string) {
    setSigningIn(provider);
    addEvent("action", `Initiating sign in with ${provider}...`);
    try {
      await signIn(provider);
    } catch (err) {
      addEvent("error", `Sign in failed: ${err}`);
    } finally {
      setSigningIn(null);
    }
  }

  async function handleSignOut() {
    addEvent("action", "Initiating sign out...");
    try {
      await signOut();
    } catch (err) {
      addEvent("error", `Sign out failed: ${err}`);
    }
  }

  const providers = [
    { id: "mock", name: "Mock Provider", icon: "🧪", available: true },
    { id: "google", name: "Google", icon: "🔵", available: false },
    { id: "apple", name: "Apple", icon: "🍎", available: false },
    { id: "email", name: "Email/Password", icon: "✉️", available: false },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">🔐 Auth Playground</h1>
        <p className="page-subtitle">
          Test authentication flows with different providers
        </p>
      </div>

      <div className="grid grid-2">
        {/* Auth Status Card */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-icon">👤</span>
              Current Session
            </div>
            <span className={`card-badge ${isAuthenticated ? "success" : "warning"}`}>
              {isLoading ? "Loading..." : isAuthenticated ? "Authenticated" : "Not Authenticated"}
            </span>
          </div>

          {isAuthenticated && user ? (
            <div>
              <div className="user-info">
                <div className="user-avatar">
                  {user.name?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="user-details">
                  <div className="user-name">{user.name}</div>
                  <div className="user-email">{user.email}</div>
                  <div className="user-meta">
                    Provider: {user.provider} | ID: {user.id}
                  </div>
                </div>
              </div>
              <button
                className="btn btn-danger mt-md"
                onClick={handleSignOut}
                disabled={isLoading}
                style={{ width: "100%" }}
              >
                Sign Out
              </button>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-muted)" }}>
              <div style={{ fontSize: "3rem", marginBottom: 16 }}>🔒</div>
              <p>No active session</p>
              <p style={{ fontSize: "0.85rem", marginTop: 8 }}>
                Sign in with a provider below
              </p>
            </div>
          )}
        </div>

        {/* Providers Card */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-icon">🔑</span>
              Auth Providers
            </div>
          </div>

          <div className="flex" style={{ flexDirection: "column", gap: 12 }}>
            {providers.map((provider) => (
              <div
                key={provider.id}
                className={`provider-card ${!provider.available || isAuthenticated ? "disabled" : ""}`}
                onClick={() => {
                  if (provider.available && !isAuthenticated) {
                    handleSignIn(provider.id);
                  }
                }}
              >
                <div className="provider-icon">{provider.icon}</div>
                <div className="provider-info">
                  <div className="provider-name">{provider.name}</div>
                  <div className="provider-status">
                    {!provider.available
                      ? "Not configured"
                      : signingIn === provider.id
                        ? "Signing in..."
                        : isAuthenticated
                          ? "Already signed in"
                          : "Click to sign in"}
                  </div>
                </div>
                {provider.available && (
                  <span className="card-badge success">Available</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Log */}
      <div className="card mt-lg">
        <div className="card-header">
          <div className="card-title">
            <span className="card-icon">📋</span>
            Event Log
          </div>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setEvents([])}
            disabled={events.length === 0}
          >
            Clear
          </button>
        </div>
        <div className="event-log">
          {events.length === 0 ? (
            <div className="event-empty">
              No events yet. Try signing in or out to see events.
            </div>
          ) : (
            events.map((event) => (
              <div key={event.id} className="event-item">
                <span className="event-time">{event.time}</span>
                <span className="event-type">{event.type}</span>
                <span className="event-data">{event.data}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Code Example */}
      <div className="card mt-lg">
        <div className="card-header">
          <div className="card-title">
            <span className="card-icon">💻</span>
            Code Example
          </div>
        </div>
        <div className="code-block">
{`import { useAuth } from "@godgpt/react";

function AuthComponent() {
  const { isAuthenticated, user, signIn, signOut } = useAuth();

  if (isAuthenticated) {
    return (
      <div>
        <p>Welcome, {user.name}!</p>
        <button onClick={signOut}>Sign Out</button>
      </div>
    );
  }

  return <button onClick={() => signIn("google")}>Sign In</button>;
}`}
        </div>
      </div>
    </div>
  );
}
